/**
 * Release Notes & Clips
 *
 * Display the AI-generated release notes for a Release QA run,
 * along with auto-extracted video clips and screenshots from passing workflows.
 */

import React from 'react';
import { Text, Flex, Markdown, LoadingOrbit } from '@chatsdet/ui';
import { FileText, FilmSlate } from '@phosphor-icons/react';
import type { ReleaseQARunStatus } from '../releaseTypes';
import { isActiveStatus } from '../releaseTypes';
import { useReleaseClips, type ReleaseClip } from '../hooks/useReleaseClips';

type ReleaseReportProps = {
  releaseRunId: string;
  runStatus: ReleaseQARunStatus;
  releaseNotesMarkdown: string | null;
  releaseNotesCompletedAt: string | null;
  commitsAnalyzed: number;
};

export const ReleaseReport: React.FC<ReleaseReportProps> = ({
  releaseRunId,
  runStatus,
  releaseNotesMarkdown,
  releaseNotesCompletedAt,
  commitsAnalyzed,
}) => {
  // Poll for clips while the run is active or notes are ready but clips haven't arrived
  const expectMoreClips = isActiveStatus(runStatus) || !!releaseNotesMarkdown;
  const { data: clips } = useReleaseClips(releaseRunId, expectMoreClips);

  // If notes are ready, render them
  if (releaseNotesMarkdown) {
    return (
      <div className="space-y-6">
        <div className="p-6 bg-card rounded-lg border border-border">
          {/* Header bar */}
          <Flex align="center" justify="between" className="mb-4 pb-3 border-b border-border">
            <Flex align="center" gap="sm">
              <FileText size={16} className="text-muted-foreground" />
              <Text size="sm" weight="medium">Release Notes</Text>
            </Flex>
            {releaseNotesCompletedAt && (
              <Text size="xs" className="text-muted-foreground">
                Generated {new Date(releaseNotesCompletedAt).toLocaleString()}
              </Text>
            )}
          </Flex>
          <Markdown className="prose prose-base max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground">
            {releaseNotesMarkdown}
          </Markdown>
        </div>

        {/* Clips section */}
        {clips && clips.length > 0 && <ReleaseClipsSection clips={clips} />}
      </div>
    );
  }

  // Show loading state while run is still active and notes not ready
  if (isActiveStatus(runStatus)) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
        <LoadingOrbit size="lg" className="mx-auto mb-4" />
        <Text className="text-muted-foreground">Generating release notes...</Text>
        <Text size="sm" className="text-muted-foreground mt-1">
          Release notes will appear here once the analysis is complete
        </Text>
      </div>
    );
  }

  // For failed/canceled runs, show appropriate message
  if (runStatus === 'failed' || runStatus === 'canceled') {
    return (
      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
        <FileText size={48} className="mx-auto text-muted-foreground mb-4" />
        <Text className="text-muted-foreground">
          {runStatus === 'canceled' ? 'Run was canceled' : 'Run failed'}
        </Text>
        <Text size="sm" className="text-muted-foreground mt-1">
          Release notes are not available for this run
        </Text>
      </div>
    );
  }

  // Completed but no notes were generated
  const wasSkipped = commitsAnalyzed === 0;
  return (
    <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
      <FileText size={48} className="mx-auto text-muted-foreground mb-4" />
      <Text className="text-muted-foreground">
        {wasSkipped ? 'No release notes generated' : 'No release notes available'}
      </Text>
      <Text size="sm" className="text-muted-foreground mt-1">
        {wasSkipped
          ? 'No new commits since the previous release run — there was nothing to summarize.'
          : 'Release notes were not generated for this run'}
      </Text>
    </div>
  );
};

/**
 * Renders a grid of release clips and screenshots.
 */
function ReleaseClipsSection({ clips }: { clips: ReleaseClip[] }) {
  return (
    <div className="p-6 bg-card rounded-lg border border-border">
      <Flex align="center" gap="sm" className="mb-4 pb-3 border-b border-border">
        <FilmSlate size={16} className="text-muted-foreground" />
        <Text size="sm" weight="medium">Feature Clips</Text>
        <Text size="xs" className="text-muted-foreground ml-auto">
          {clips.length} {clips.length === 1 ? 'clip' : 'clips'}
        </Text>
      </Flex>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {clips.map((clip) => (
          <ReleaseClipCard key={clip.workflowRunId} clip={clip} />
        ))}
      </div>
    </div>
  );
}

/**
 * A single clip or screenshot card.
 */
function ReleaseClipCard({ clip }: { clip: ReleaseClip }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
      {clip.metadata.outputType === 'clip' ? (
        <video
          src={clip.url}
          autoPlay
          muted
          loop
          playsInline
          controls
          className="w-full aspect-video object-contain bg-black"
        />
      ) : (
        <img
          src={clip.url}
          alt={clip.workflowName ?? 'Release screenshot'}
          className="w-full aspect-video object-contain bg-black"
        />
      )}
      <div className="p-3">
        {clip.workflowName && (
          <Text size="sm" weight="medium" className="mb-1">
            {clip.workflowName}
          </Text>
        )}
        <Text size="xs" className="text-muted-foreground">
          {clip.metadata.rationale}
        </Text>
      </div>
    </div>
  );
}
